'use client';

// Reusable search input box component based on Figma design

import React, { useState, JSX, useRef, useEffect, useMemo } from 'react';
import { tv } from 'tailwind-variants';
import { getTestProps } from 'lib/testing/utils';
import { RemixIcon } from 'helpers/RemixIcon';
import {
  Context,
  getWidgetData,
  SearchWidgetItem,
  WidgetRequestData,
} from '@sitecore-cloudsdk/search/browser';
import { useSearchInitialization } from 'lib/context/SearchInitializationContext';
import {
  mapResultsToTrackEntities,
  trackSearchSuggestionClick,
  trackSearchWidgetView,
} from 'lib/search/analytics';

export type SuggestionItem = {
  id: string;
  name: string;
  description: string;
  type: string;
  source_id: string;
  url: string;
};

export type SearchInputBoxProps = {
  value?: string;
  placeholder?: string;
  onSearch?: (searchTerm: string) => void;
  onClear?: () => void;
  className?: string;
  label?: string;
  disabled?: boolean;
  maxLength?: number;
  onSuggestionSelect?: (suggestion: SuggestionItem, index?: number) => void;
  showSuggestions?: boolean;
  minCharactersForSuggestions?: number;
  widgetId?: string;
  suggestionName?: string;
  sources?: string[];
  entity?: string;
};

const SearchInputBox = ({
  value = '',
  placeholder = 'Search...',
  onSearch,
  onClear,
  disabled = false,
  maxLength,
  onSuggestionSelect,
  showSuggestions = true,
  minCharactersForSuggestions = 3,
  widgetId = 'rfkid_6',
  suggestionName = 'title_auto_complete',
  sources,
  entity = 'content',
}: SearchInputBoxProps): JSX.Element => {
  const [searchValue, setSearchValue] = useState(value);
  const [suggestions, setSuggestions] = useState<SuggestionItem[]>([]);
  const [isFocused, setIsFocused] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const clearButtonRef = useRef<HTMLButtonElement>(null);
  const isClearingRef = useRef(false);
  const { isSearchInitialized } = useSearchInitialization();

  const removeSpecialCharacters = (value: string) => {
    return value.replace(/[^a-zA-Z0-9- ]/g, '');
  };

  useEffect(() => {
    setSearchValue(value);
  }, [value]);

  // Update dropdown visibility based on input and suggestions
  useEffect(() => {
    const shouldShow: boolean =
      showSuggestions &&
      isFocused &&
      searchValue &&
      searchValue.length >= minCharactersForSuggestions &&
      suggestions.length > 0
        ? true
        : false;
    setShowDropdown(shouldShow);
    setSelectedSuggestionIndex(-1);
  }, [searchValue, isFocused, suggestions, showSuggestions, minCharactersForSuggestions]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = removeSpecialCharacters(e.target.value);
    setSearchValue(newValue);
  };

  const handleClear = () => {
    isClearingRef.current = true;
    setSearchValue('');
    onClear?.();
    // Use setTimeout to ensure focus happens after any blur events
    setTimeout(() => {
      inputRef.current?.focus();
      setIsFocused(true);
      // Reset the clearing flag after a short delay
      setTimeout(() => {
        isClearingRef.current = false;
      }, 200);
    }, 0);
  };

  const handleSearch = () => {
    if (searchValue.trim()) {
      onSearch?.(searchValue.trim());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'Enter':
        e.preventDefault();
        if (showDropdown && selectedSuggestionIndex >= 0) {
          // Select the highlighted suggestion
          const selectedSuggestion = suggestions[selectedSuggestionIndex];
          if (selectedSuggestion.url) {
            // Navigate to the suggestion's URL
            onSuggestionSelect?.(selectedSuggestion, selectedSuggestionIndex);
            window.location.href = selectedSuggestion.url;
          } else {
            // Fallback: set search value and trigger search
            setSearchValue(selectedSuggestion.name);
            onSuggestionSelect?.(selectedSuggestion, selectedSuggestionIndex);
            setShowDropdown(false);
            setSelectedSuggestionIndex(-1);
            handleSearch();
          }
        } else {
          handleSearch();
        }
        break;

      case 'ArrowDown':
        e.preventDefault();
        if (showDropdown && suggestions.length > 0) {
          setSelectedSuggestionIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
        } else if (!showDropdown && searchValue.trim()) {
          // Show dropdown if not visible and has search value
          setShowDropdown(true);
          setSelectedSuggestionIndex(0);
        }
        break;

      case 'ArrowUp':
        e.preventDefault();
        if (showDropdown && suggestions.length > 0) {
          setSelectedSuggestionIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
        }
        break;

      case 'Escape':
        e.preventDefault();
        setShowDropdown(false);
        setSelectedSuggestionIndex(-1);
        inputRef.current?.blur();
        break;

      case 'Tab':
        // Allow normal tab behavior but close dropdown
        if (showDropdown) {
          setShowDropdown(false);
          setSelectedSuggestionIndex(-1);
        }
        break;

      case 'Home':
        if (showDropdown && suggestions.length > 0) {
          e.preventDefault();
          setSelectedSuggestionIndex(0);
        }
        break;

      case 'End':
        if (showDropdown && suggestions.length > 0) {
          e.preventDefault();
          setSelectedSuggestionIndex(suggestions.length - 1);
        }
        break;

      default:
        // For any other key, reset selection
        if (showDropdown) {
          setSelectedSuggestionIndex(-1);
        }
        break;
    }
  };

  const handleSuggestionClick = (suggestion: SuggestionItem, index: number, e?: React.MouseEvent) => {
    // Prevent default to avoid any potential issues
    e?.preventDefault();
    e?.stopPropagation();

    // Immediately hide dropdown to prevent blur race condition
    setShowDropdown(false);
    setSelectedSuggestionIndex(-1);

    const suggestionIndex =
      index >= 0
        ? index
        : suggestions.findIndex(
            (item) => item.id === suggestion.id || item.name === suggestion.name
          );

    void trackSearchSuggestionClick({
      widgetId,
      filters: [
        {
          name: suggestionName,
          title: suggestionName,
          value: suggestion.name || suggestion.id,
          valuePosition: suggestionIndex >= 0 ? suggestionIndex : 0,
          displayName: suggestion.name || suggestion.id,
        },
      ],
      request: {
        keyword: searchValue.trim() || undefined,
      },
    });

    // Notify parent (e.g. SearchResult) — parent owns GTM search_click fields
    onSuggestionSelect?.(suggestion, suggestionIndex >= 0 ? suggestionIndex : undefined);

    if (suggestion?.url && typeof window !== 'undefined') {
      // Navigate to the suggestion's URL
      try {
        window.location.href = suggestion.url;
      } catch (error) {
        console.error('Error navigating to suggestion URL:', error);
        // Fallback: set search value and trigger search
        setSearchValue(suggestion.name);
        handleSearch();
      }
    } else {
      // Fallback: set search value and trigger search
      setSearchValue(suggestion?.name || '');
      handleSearch();
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Check if the blur is caused by clicking on a suggestion
    // If the relatedTarget (the element receiving focus) is within the dropdown, don't hide it
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (relatedTarget && dropdownRef.current?.contains(relatedTarget)) {
      return; // Don't hide dropdown if clicking inside it
    }

    // Check if the blur is caused by clicking the clear button
    if (relatedTarget && clearButtonRef.current?.contains(relatedTarget)) {
      return; // Don't hide dropdown if clicking the clear button
    }

    // If we're in the process of clearing, don't handle blur
    if (isClearingRef.current) {
      return;
    }

    // Delay hiding dropdown to allow for suggestion clicks
    setTimeout(() => {
      // Double-check that we're still not focused on dropdown elements or clearing
      const activeElement = document.activeElement;
      if (
        !dropdownRef.current?.contains(activeElement) &&
        !clearButtonRef.current?.contains(activeElement) &&
        !isClearingRef.current
      ) {
        setIsFocused(false);
        setShowDropdown(false);
        setSelectedSuggestionIndex(-1);
      }
    }, 150);
  };

  const sourceId = useMemo(() => {
    // Use provided sources prop if available, otherwise fall back to environment variable
    if (sources && sources.length > 0) {
      return sources.filter((s) => typeof s === 'string' && s.trim() !== '');
    }
    return process.env.NEXT_PUBLIC_SEARCH_GLOBAL_SOURCE_ID
      ? [process.env.NEXT_PUBLIC_SEARCH_GLOBAL_SOURCE_ID]
      : undefined;
  }, [sources]);

  useEffect(() => {
    if (!isSearchInitialized) {
      return;
    }

    if (searchValue.trim() !== '' && searchValue.trim().length >= minCharactersForSuggestions) {
      const fetchData = async () => {
        const context = new Context({
          locale: {
            language: 'en',
            country: 'us',
          },
        });

        const searchWidget = new SearchWidgetItem(
          entity,
          widgetId,
          {
            limit: 8,
            offset: 0,
            content: {},
            facet: {
              all: false,
            },
            query: { keyphrase: searchValue },
            suggestion: [
              {
                name: suggestionName,
                max: 8,
                keyphraseFallback: true,
              },
            ],
          },
          sourceId && sourceId.length > 0 ? (sourceId as [string, ...string[]]) : undefined
        );
        const apiData = await getWidgetData(new WidgetRequestData([searchWidget]), context);
        const suggestionResultData = apiData?.widgets[0]?.content ?? [];
        setSuggestions(suggestionResultData as SuggestionItem[]);

        if (suggestionResultData.length > 0) {
          void trackSearchWidgetView({
            widgetId,
            entities: mapResultsToTrackEntities(
              suggestionResultData as Array<{ id?: string; url?: string; source_id?: string }>,
              entity
            ),
            request: {
              keyword: searchValue.trim(),
              numResults: suggestionResultData.length,
              numRequested: 8,
            },
          });
        }
      };
      fetchData();
    } else {
      // Clear suggestions when input is below minimum character requirement
      setSuggestions([]);
    }
  }, [searchValue, minCharactersForSuggestions, widgetId, sourceId, suggestionName, entity, isSearchInitialized]);

  const {
    base,
    searchInputWrapper,
    inputWrapper,
    searchIcon,
    input,
    actionButtonWrapper,
    clearButton,
    searchButton,
    dropdown,
    suggestionHeader,
    suggestionItemsWrapper,
    suggestionItem,
    suggestionItemIcon,
    suggestionItemText,
  } = TAILWIND_VARIANTS();

  return (
    <div className={base()} {...getTestProps('search-input-box')}>
      <div className={searchInputWrapper()}>
        <div className={inputWrapper()}>
          <RemixIcon name="ri-search-line" size="md" className={searchIcon()} />
          <input
            id="search-result-input"
            name="search-result-input"
            ref={inputRef}
            type="text"
            value={searchValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={placeholder}
            disabled={disabled}
            maxLength={maxLength}
            className={input()}
            role="combobox"
            aria-label="Search"
            aria-expanded={showDropdown}
            aria-haspopup="listbox"
            aria-autocomplete="list"
            aria-controls={showDropdown ? 'search-suggestions' : undefined}
            aria-activedescendant={
              showDropdown && selectedSuggestionIndex >= 0
                ? `suggestion-${suggestions[selectedSuggestionIndex]?.id || selectedSuggestionIndex}`
                : undefined
            }
            aria-owns={showDropdown ? 'search-suggestions' : undefined}
            aria-describedby={showDropdown ? 'suggestions-header' : undefined}
            {...getTestProps('search-input')}
          />
        </div>
        {searchValue && (
          <div className={actionButtonWrapper()}>
            <button
              ref={clearButtonRef}
              type="button"
              onClick={handleClear}
              className={clearButton()}
              disabled={disabled}
              aria-label="Clear search"
              {...getTestProps('search-clear')}
            >
              <RemixIcon name="ri-close-line" size="md" />
            </button>
            <button
              type="button"
              onClick={handleSearch}
              disabled={disabled || !searchValue.trim()}
              className={searchButton()}
              aria-label="Search"
              {...getTestProps('search-submit')}
            >
              <RemixIcon name="ri-arrow-right-line" size="md" />
            </button>
          </div>
        )}
      </div>

      {showDropdown && (
        <div
          ref={dropdownRef}
          className={dropdown()}
          role="listbox"
          id="search-suggestions"
          aria-label={`${suggestions.length} search suggestions`}
          aria-live="polite"
          {...getTestProps('suggestions-dropdown')}
        >
          <div className={suggestionHeader()} id="suggestions-header">
            Suggestions ({suggestions.length})
          </div>
          <div className={suggestionItemsWrapper()}>
            {suggestions.map((suggestion, index) => (
              <button
                key={suggestion.id || `suggestion-${index}`}
                id={`suggestion-${suggestion.id || index}`}
                type="button"
                className={suggestionItem({ isSelected: index === selectedSuggestionIndex })}
                onMouseDown={(e) => {
                  // Use onMouseDown to handle click before blur event fires
                  e.preventDefault(); // Prevent input blur
                  handleSuggestionClick(suggestion, index, e);
                }}
                role="option"
                aria-label={suggestion.name}
                aria-selected={index === selectedSuggestionIndex}
                aria-describedby="suggestions-header"
                tabIndex={-1}
                {...getTestProps(`suggestion-${suggestion.id || index}`)}
              >
                <RemixIcon
                  name="ri-search-line"
                  size="md"
                  className={suggestionItemIcon({ isSelected: index === selectedSuggestionIndex })}
                />
                <span
                  className={suggestionItemText({ isSelected: index === selectedSuggestionIndex })}
                >
                  {suggestion.name}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchInputBox;

const TAILWIND_VARIANTS = tv({
  slots: {
    base: ['w-full', 'relative', 'z-[10]'],
    searchInputWrapper: [
      'w-full',
      'h-12',
      'px-spacing-spacing-8',
      'flex',
      'flex-row',
      'justify-between',
      'gap-spacing-spacing-8',
      'bg-color-grayscale-white',
      'border',
      'border-color-border-border-hover',
      'rounded-border-radius-radius-2',
      'relative',
    ],
    inputWrapper: [
      'w-full',
      'h-full',
      'flex',
      'flex-row',
      'flex-start',
      'py-spacing-spacing-12',
      'gap-spacing-spacing-8',
    ],
    searchIcon: ['text-color-grayscale-black'],
    input: [
      'w-full',
      'h-full',
      'flex-1',
      'bg-color-grayscale-white',
      'border-none',
      'outline-none',
      'font-typography-font-family-body',
      'text-p-color-text',
      'text-typography-font-size-body-medium',
      'leading-typography-line-height-body-medium',
      'font-normal',
      'placeholder:text-p-color-text-secondary',
      'placeholder:font-typography-font-family-body',
      'placeholder:text-typography-font-size-body-medium',
      'placeholder:leading-typography-line-height-body-medium',
      'placeholder:italic',
    ],
    actionButtonWrapper: [
      'w-fit',
      'flex',
      'flex-row',
      'items-center',
      'justify-between',
      'gap-spacing-spacing-4',
      'py-spacing-spacing-8',
    ],
    clearButton: [
      'flex',
      'items-center',
      'justify-center',
      'w-8',
      'h-8',
      'p-spacing-spacing-12',
      'bg-component-button-secondary-bg',
      'rounded-general-border-radius-button',
      'border',
      'border-component-button-secondary-border',
      'border-general-border-width-button',
      'text-component-button-secondary-icon',
    ],
    searchButton: [
      'flex',
      'items-center',
      'justify-center',
      'w-8',
      'h-8',
      'p-spacing-spacing-12',
      'rounded-general-border-radius-button',
      'bg-component-button-primary-bg',
      'text-component-button-primary-text',
    ],
    dropdown: [
      'absolute',
      'top-full',
      'left-0',
      'right-0',
      'mt-2',
      'flex',
      'flex-col',
      'gap-padding-less',
      'p-padding-less',
      'bg-color-grayscale-white',
      'border',
      'border-p-color-border-hover',
      'rounded-[16px]',
      'max-h-[380px]',
      'overflow-y-auto',
    ],
    suggestionHeader: [
      'w-full',
      'text-left',
      'font-typography-font-family-body',
      'text-typography-font-size-body-medium',
      'text-color-text-text',
      'font-bold',
      'leading-typography-line-height-body-medium',
    ],
    suggestionItemsWrapper: ['w-full', 'flex', 'flex-col', 'gap-padding-micro'],
    suggestionItem: [
      'w-full',
      'h-full',
      'flex',
      'flex-row',
      'items-center',
      'gap-spacing-spacing-8',
      'justify-start',
      'p-spacing-spacing-2',
      'bg-color-grayscale-white',
      'rounded-general-border-radius-none',
      'font-typography-font-family-body',
      'text-typography-font-size-body-medium',
      'text-color-text-text',
      'hover:bg-color-core-color-4-skyblue-lightTint',
      'hover:text-color-core-color-4-skyblue-darkTint',
      'focus:bg-color-core-color-4-skyblue-lightTint',
      'focus:text-color-core-color-4-skyblue-darkTint',
      'focus:outline-border-p-color-border-focus',
      'transition-colors',
      'duration-150',
    ],
    suggestionItemIcon: ['w-6', 'h-6', 'text-component-button-secondary-icon'],
    suggestionItemText: [
      'text-left',
      'text-component-button-tertiary-text',
      'font-typography-font-family-body',
      'text-typography-font-size-body-medium',
      'font-semibold',
      'leading-typography-line-height-xxsmall',
    ],
  },
  variants: {
    isSelected: {
      true: {
        suggestionItem: [
          'bg-color-core-color-4-skyblue-lightTint',
          'text-color-core-color-4-skyblue-darkTint',
          'font-semibold',
          'ring-2',
          'ring-color-core-color-4-skyblue-lightTint',
          'ring-offset-1',
        ],
        suggestionItemIcon: ['text-color-core-color-4-skyblue-darkTint'],
        suggestionItemText: ['text-color-core-color-4-skyblue-darkTint', 'font-semibold'],
      },
      false: {
        suggestionItem: ['bg-color-grayscale-white'],
        suggestionItemIcon: ['text-component-button-secondary-icon'],
        suggestionItemText: ['text-component-button-tertiary-text'],
      },
    },
  },
});
